import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { RotateCcw } from 'lucide-react';
import { useUserAsset, useDownloadUserAsset, useDownloadPublishedUserAsset } from '../hooks/useUserAssets';
import { format } from 'date-fns';
import { getAssetTypeLabel } from '../constants';
import toast from 'react-hot-toast';
import {
  getAssetCover,
  getAssetFlags,
  getAssetTitle,
  getDownloadErrorMessage,
  getUserContacts,
} from '../assetDetail';
import ReactivateAssetDialog from '../components/ReactivateAssetDialog';

const UserAssetDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { data: asset, isLoading, error } = useUserAsset(id!);
  const downloadMutation = useDownloadUserAsset();
  const downloadPublishedMutation = useDownloadPublishedUserAsset();
  const [isReactivateOpen, setIsReactivateOpen] = useState(false);

  const handleDownload = async () => {
    if (!asset) return;
    
    try {
      const blob = await downloadMutation.mutateAsync(asset.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${getAssetTitle(asset) || 'asset'}-${asset.id}.json`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      toast.error(await getDownloadErrorMessage(error));
    }
  };

  const handleDownloadPublished = async () => {
    if (!asset) return;
    
    try {
      const blob = await downloadPublishedMutation.mutateAsync(asset.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${getAssetTitle(asset) || 'asset'}-published-${asset.id}.json`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      toast.error(await getDownloadErrorMessage(error));
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      active: { color: 'bg-green-100 text-green-800', label: 'Hoạt động' },
      expired: { color: 'bg-yellow-100 text-yellow-800', label: 'Hết hạn' },
      revoked: { color: 'bg-red-100 text-red-800', label: 'Bị thu hồi' },
      suspended: { color: 'bg-gray-100 text-gray-800', label: 'Tạm dừng' },
    };
    
    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.active;
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const getAccessLevelLabel = (level: string) => {
    const levelLabels = {
      full: 'Đầy đủ',
      read_only: 'Chỉ đọc',
      preview: 'Xem trước',
    };
    return levelLabels[level as keyof typeof levelLabels] || level;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !asset) {
    return (
      <div className="text-center py-12">
        <div className="text-red-600 mb-4">
          <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Không tìm thấy tài sản</h3>
        <p className="text-gray-500 mb-4">Tài sản người dùng không tồn tại hoặc đã bị xóa.</p>
        <Link
          to="/admin/user-assets"
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
        >
          Quay lại danh sách
        </Link>
      </div>
    );
  }

  const title = getAssetTitle(asset);
  const cover = getAssetCover(asset);
  const contacts = getUserContacts(asset.user);
  const { isActive, isExpired, canDownload } = getAssetFlags(asset);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-start justify-between">
          <div className="flex items-start space-x-4">
            {cover && (
              <img
                className="h-20 w-16 rounded object-cover"
                src={cover}
                alt={title}
              />
            )}
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {title || 'Tài sản không có tên'}
              </h1>
              <p className="text-gray-600 mt-1">
                {getAssetTypeLabel(asset.asset_type)} • {asset.user?.name || 'Người dùng không xác định'}
              </p>
              <div className="mt-2">
                {getStatusBadge(asset.status)}
              </div>
            </div>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={handleDownload}
              disabled={downloadMutation.isPending || !canDownload}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {downloadMutation.isPending ? 'Đang tải...' : 'Tải xuống'}
            </button>
            <button
              onClick={handleDownloadPublished}
              disabled={downloadPublishedMutation.isPending || !canDownload}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {downloadPublishedMutation.isPending ? 'Đang tải...' : 'Tải xuống (Published)'}
            </button>
            <Link
              to="/admin/user-assets"
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Quay lại
            </Link>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Asset Information */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Information */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Thông tin Cơ bản</h3>
            <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <dt className="text-sm font-medium text-gray-500">ID Tài sản</dt>
                <dd className="mt-1 text-sm text-gray-900 font-mono">{asset.id}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Loại Tài sản</dt>
                <dd className="mt-1 text-sm text-gray-900">{getAssetTypeLabel(asset.asset_type)}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Cấp độ Truy cập</dt>
                <dd className="mt-1 text-sm text-gray-900">{getAccessLevelLabel(asset.access_level)}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Trạng thái</dt>
                <dd className="mt-1">{getStatusBadge(asset.status)}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Gán lúc</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {format(new Date(asset.assigned_at), 'dd/MM/yyyy HH:mm')}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Hết hạn</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {asset.expires_at 
                    ? format(new Date(asset.expires_at), 'dd/MM/yyyy HH:mm')
                    : 'Không giới hạn'
                  }
                </dd>
              </div>
            </dl>
          </div>

          {/* User Information */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Thông tin Người dùng</h3>
            <div className="flex items-center space-x-4">
              <div className="flex-shrink-0 h-12 w-12">
                <div className="h-12 w-12 rounded-full bg-gray-300 flex items-center justify-center">
                  <span className="text-lg font-medium text-gray-700">
                    {asset.user?.name?.charAt(0) || 'U'}
                  </span>
                </div>
              </div>
              <div>
                <div className="text-lg font-medium text-gray-900">
                  {asset.user?.name || 'Người dùng không xác định'}
                </div>
                <div className="text-sm text-gray-500">
                  {contacts.length > 0 ? contacts.join(' • ') : 'Chưa có thông tin liên hệ'}
                </div>
                {asset.user?.code && (
                  <div className="text-sm text-gray-500">Mã: {asset.user.code}</div>
                )}
                <div className="text-sm text-gray-500">
                  ID: {asset.user_id}
                </div>
              </div>
            </div>
          </div>

          {/* Book / Collection Information */}
          {title && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Thông tin {asset.asset_type === 'collection' ? 'Bộ sách' : 'Sách'}
              </h3>
              <div className="flex items-start space-x-4">
                {cover && (
                  <img
                    className="h-24 w-18 rounded object-cover"
                    src={cover}
                    alt={title}
                  />
                )}
                <div className="flex-1">
                  <h4 className="text-lg font-medium text-gray-900">{title}</h4>
                  {asset.asset_type !== 'collection' && asset.book?.description && (
                    <p className="text-sm text-gray-600 mt-2">{asset.book.description}</p>
                  )}
                  <div className="mt-2 text-sm text-gray-500">
                    ID: {asset.asset_id}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Activation Code Information */}
          {asset.activation_code && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Thông tin Mã Kích hoạt</h3>
              <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Mã Kích hoạt</dt>
                  <dd className="mt-1 text-sm text-gray-900 font-mono">{asset.activation_code.code}</dd>
                </div>
                {asset.activation_code.sku && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">SKU</dt>
                    <dd className="mt-1 text-sm text-gray-900">{asset.activation_code.sku}</dd>
                  </div>
                )}
              </dl>
            </div>
          )}

          {/* Metadata */}
          {asset.metadata && Object.keys(asset.metadata).length > 0 && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Metadata</h3>
              <pre className="bg-gray-50 rounded-lg p-4 text-sm text-gray-900 overflow-x-auto">
                {JSON.stringify(asset.metadata, null, 2)}
              </pre>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Download Statistics */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Thống kê Tải xuống</h3>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Số lần tải xuống</span>
                  <span className="font-medium text-gray-900">{asset.download_count}</span>
                </div>
                {/* Hạn mức tính theo số thiết bị: một máy tải lại hay cập nhật
                    bao nhiêu lần cũng chỉ chiếm một suất. Đối chiếu số lần tải
                    với giới hạn sẽ báo vượt hạn mức trong khi server vẫn cho tải. */}
                <div className="flex justify-between text-sm mt-2">
                  <span className="text-gray-500">Số thiết bị đã tải</span>
                  <span className="font-medium text-gray-900">{asset.device_count}</span>
                </div>
                {asset.max_download && (
                  <div className="mt-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Số thiết bị tối đa</span>
                      <span className="font-medium text-gray-900">{asset.max_download}</span>
                    </div>
                    <div className="mt-1 w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full"
                        style={{ width: `${Math.min(asset.device_count / asset.max_download, 1) * 100}%` }}
                      ></div>
                    </div>
                  </div>
                )}
              </div>
              {asset.downloads_remaining !== undefined && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Còn lại</span>
                  <span className="font-medium text-gray-900">
                    {asset.downloads_remaining === null ? 'Không giới hạn' : asset.downloads_remaining}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Status Information */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Trạng thái</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Hoạt động</span>
                <span className={`text-sm font-medium ${isActive ? 'text-green-600' : 'text-red-600'}`}>
                  {isActive ? 'Có' : 'Không'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Hết hạn</span>
                <span className={`text-sm font-medium ${isExpired ? 'text-red-600' : 'text-green-600'}`}>
                  {isExpired ? 'Có' : 'Không'}
                </span>
              </div>
              {asset.days_until_expiry !== null && asset.days_until_expiry !== undefined && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">Còn lại (ngày)</span>
                  <span className={`text-sm font-medium ${
                    asset.days_until_expiry < 7 ? 'text-red-600' : 
                    asset.days_until_expiry < 30 ? 'text-yellow-600' : 'text-green-600'
                  }`}>
                    {asset.days_until_expiry}
                  </span>
                </div>
              )}
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Có thể tải xuống</span>
                <span className={`text-sm font-medium ${canDownload ? 'text-green-600' : 'text-red-600'}`}>
                  {canDownload ? 'Có' : 'Không'}
                </span>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Thao tác Nhanh</h3>
            <div className="space-y-2">
              {asset.status === 'revoked' && (
                <button
                  type="button"
                  onClick={() => setIsReactivateOpen(true)}
                  className="w-full inline-flex items-center justify-center gap-1 px-4 py-2 border border-blue-300 text-blue-700 rounded-lg hover:bg-blue-50 transition-colors text-sm"
                >
                  <RotateCcw className="h-4 w-4" />
                  Kích hoạt lại
                </button>
              )}
              <button
                onClick={handleDownload}
                disabled={downloadMutation.isPending || !canDownload}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
              >
                Tải xuống Tài sản
              </button>
              <button
                onClick={handleDownloadPublished}
                disabled={downloadPublishedMutation.isPending || !canDownload}
                className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
              >
                Tải xuống (Published)
              </button>
              <button className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm">
                Chỉnh sửa Tài sản
              </button>
              <button className="w-full px-4 py-2 border border-red-300 text-red-700 rounded-lg hover:bg-red-50 transition-colors text-sm">
                Xóa Tài sản
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Kích hoạt lại: xác nhận + lý do, hoặc gia hạn trước nếu đã hết hạn */}
      <ReactivateAssetDialog asset={asset} open={isReactivateOpen} onOpenChange={setIsReactivateOpen} />
    </div>
  );
};

export default UserAssetDetailPage;
export { UserAssetDetailPage };
